import { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/api';
import { formatEat, formatEatDate, formatEatTime, nowEatString, todayEatIso } from '../../utils/datetime';
import { Button, Input, Select, StatusBadge, Spinner, Card } from '../shared/UI';
import { ReportVisuals } from './ReportVisuals';
import { DailyTrendChart } from './DailyTrendChart';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'Waiting', label: 'Waiting' },
  { value: 'Open', label: 'Open' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Skipped', label: 'Skipped' },
];

const VIEW_MODES = [
  { value: 'table', label: 'Table' },
  { value: 'charts', label: 'Charts' },
  { value: 'both', label: 'Both' },
];

function csvEscape(val) {
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function fmtDuration(secs) {
  if (secs === null || secs === undefined) return '\u2014';
  const m = Math.floor(secs / 60), s = secs % 60;
  return `${m}m ${s}s`;
}

function flattenServices(nodes, depth = 0) {
  let result = [];
  for (const n of nodes) {
    const prefix = '\u00A0\u00A0'.repeat(depth);
    const label = depth > 0 ? `${prefix}\u2514 ${n.name}` : n.name;
    if (n.children && n.children.length > 0) {
      result.push({ value: String(n.id), label });
      result = result.concat(flattenServices(n.children, depth + 1));
    } else {
      result.push({ value: String(n.id), label });
    }
  }
  return result;
}

export function ReportingModule() {
  const [tickets, setTickets] = useState([]);
  const [services, setServices] = useState([]);
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('both');
  const [filters, setFilters] = useState({ status: '', service_id: '', counter_id: '', date_from: '', date_to: '', search: '' });
  const [orgName, setOrgName] = useState('');
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(true);
  const chartsRef = useRef(null);
  const trendRef = useRef(null);

  useEffect(() => {
    api.get('/api/admin/config').then(cfg => setOrgName(cfg.org_name || '')).catch(() => {});
    api.get('/api/services/?active=true').then(allSvcs => {
      setServices(allSvcs);
    });
    api.get('/api/counters/').then(setCounters);
    api.get('/api/tickets/daily-trend?days=30').then(setTrendData).catch(() => setTrendData([])).finally(() => setTrendLoading(false));
    load();
  }, []);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const data = await api.get(`/api/tickets/?${params}`);
      setTickets(Array.isArray(data) ? data : []);
    } catch {
      setTickets([]);
    }
    setLoading(false);
  };

  const exportCSV = () => {
    const headers = ['Ticket #', 'Service', 'Counter', 'Customer', 'Phone', 'Status', 'Created', 'Wait Time', 'Service Duration', 'Comment'];
    const rows = tickets.map(t => [
      t.ticket_number, t.service_name || '',
      t.counter_name || '', t.customer_name || '', t.masked_phone || '',
      t.status, `${formatEatDate(t.created_at)} ${formatEatTime(t.created_at)}`, fmtDuration(t.wait_time_secs),
      fmtDuration(t.duration_secs), (t.agent_comment || '').replace(/,/g, ';')
    ]);
    const title = `${orgName || 'QMS'} - Ticket Report`;
    const csv = [title, '', headers, ...rows].map(r => (Array.isArray(r) ? r.map(csvEscape).join(',') : csvEscape(r))).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `qms_report_${todayEatIso()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.text(`${orgName || 'QMS'} - Ticket Report`, 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on: ${nowEatString()} | ${tickets.length} records`, 14, 22);

    let startY = 28;

    const cap = { scale: 2, backgroundColor: '#fff', useCORS: true };
    if (viewMode === 'charts' || viewMode === 'both') {
      try {
        const c = await html2canvas(chartsRef.current, cap);
        const w = pageWidth - 20;
        const h = (c.height / c.width) * w;
        doc.addImage(c.toDataURL('image/png'), 'PNG', 10, startY, w, Math.min(h, 195));
        startY += Math.min(h, 195) + 10;
      } catch (e) {
        console.warn('Charts capture failed:', e);
      }
      doc.addPage();
      const trendDataLocal = Array.isArray(trendData) ? trendData : [];
      if (trendDataLocal.length > 0) {
        const active = trendDataLocal.filter(d => d.count > 0);
        const maxVal = Math.max(...active.map(d => d.count), 0);
        const yMax = maxVal <= 70 ? 80 : Math.ceil(maxVal / 20) * 20 + 20;
        const cx = 20, cy = 30, cw = pageWidth - 40, ch = 140;
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        doc.setFontSize(14);
        doc.text('Daily QMS Ticket Trend', cx, cy - 10);
        doc.setFontSize(8);
        doc.text(`Last ${trendDataLocal.length} days`, cx, cy - 2);
        doc.setDrawColor(200);
        doc.setLineWidth(0.3);
        for (let i = 0; i <= yMax; i += Math.max(1, Math.round(yMax / 4))) {
          const y = cy + ch - (i / yMax) * ch;
          doc.line(cx, y, cx + cw, y);
          doc.setTextColor(100);
          doc.text(String(i), cx - 4, y + 2, { align: 'right' });
        }
        doc.setTextColor(59, 130, 246);
        doc.setDrawColor(59, 130, 246);
        doc.setLineWidth(1.5);
        const pts = active.map((d, i) => ({
          x: cx + (i / Math.max(active.length - 1, 1)) * cw,
          y: cy + ch - (d.count / yMax) * ch
        }));
        for (let i = 1; i < pts.length; i++) {
          doc.line(pts[i - 1].x, pts[i - 1].y, pts[i].x, pts[i].y);
        }
        pts.forEach(p => doc.circle(p.x, p.y, 1.2, 'F'));
        const labelEvery = Math.max(1, Math.ceil(active.length / 10));
        active.forEach((d, i) => {
          if (i % labelEvery !== 0 && i !== active.length - 1) return;
          const d2 = new Date(d.date + 'T00:00:00');
          const lbl = `${d2.getDate()} ${months[d2.getMonth()]}`;
          doc.setTextColor(100);
          doc.setFontSize(7);
          doc.text(lbl, cx + (i / Math.max(active.length - 1, 1)) * cw, cy + ch + 10, { align: 'center' });
        });
      } else {
        doc.text('No trend data available', cx + 10, 40);
      }
    }

    if (viewMode === 'charts') {
      doc.save(`qms_report_${todayEatIso()}.pdf`);
      return;
    }

    const headers = [['#', 'Ticket #', 'Service', 'Counter', 'Customer', 'Status', 'Created', 'Wait Time', 'Duration']];
    const rows = tickets.map((t, i) => [
      String(i + 1), t.ticket_number, t.service_name || '', t.counter_name || '',
      t.customer_name || '', t.status,
      `${formatEatDate(t.created_at)} ${formatEatTime(t.created_at)}`,
      fmtDuration(t.wait_time_secs),
      fmtDuration(t.duration_secs)
    ]);

    autoTable(doc, {
      startY,
      head: headers,
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: [10, 22, 40], textColor: [255, 255, 255] },
      styles: { fontSize: 8 },
      didDrawPage: (data) => {
        doc.setFontSize(8);
        doc.text(`Page ${data.pageNumber}`, pageWidth - 20, pageHeight - 8, { align: 'right' });
      }
    });

    doc.save(`qms_report_${todayEatIso()}.pdf`);
  };

  const serviceOpts = [{ value: '', label: 'All Services' }, ...services.map(s => ({ value: String(s.id), label: s.name }))];
  const counterOpts = [{ value: '', label: 'All Counters' }, ...counters.map(c => ({ value: String(c.id), label: c.name }))];

  const showCharts = viewMode === 'charts' || viewMode === 'both';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: 'var(--navy)' }}>Reports & Logs</h1>
          <p style={{ color: 'var(--gray-500)', marginTop: 4, fontSize: 14 }}>Searchable, filterable ticket history with analytics</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select value={viewMode} onChange={setViewMode} options={VIEW_MODES} style={{ minWidth: 110 }} />
          <Button onClick={exportCSV} variant="ghost">Export CSV</Button>
          <Button onClick={exportPDF} variant="ghost">PDF Report</Button>
        </div>
      </div>

      <Card>
        <div style={{ padding: 20 }}>
          <div className="resp-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 14 }}>
            <Input label="Search (name / ticket #)" value={filters.search} onChange={v => set('search', v)} placeholder="Search\u2026" />
            <Select label="Status" value={filters.status} onChange={v => set('status', v)} options={STATUS_OPTIONS} />
            <Select label="Service" value={filters.service_id} onChange={v => set('service_id', v)} options={serviceOpts} />
            <Select label="Counter" value={filters.counter_id} onChange={v => set('counter_id', v)} options={counterOpts} />
            <Input label="Date From" value={filters.date_from} onChange={v => set('date_from', v)} type="date" />
            <Input label="Date To" value={filters.date_to} onChange={v => set('date_to', v)} type="date" />
          </div>
          <Button onClick={load} loading={loading}>Apply Filters</Button>
        </div>
      </Card>

      {showCharts && tickets.length > 0 && !loading && (
        <>
          <div ref={chartsRef} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <ReportVisuals tickets={tickets} counters={counters} />
          </div>
          <div ref={trendRef} style={{ width: '100%' }}>
            <DailyTrendChart trendData={trendData} trendLoading={trendLoading} />
          </div>
        </>
      )}

      {(viewMode === 'table' || viewMode === 'both') && (
        <Card>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>
              {tickets.length} records
            </span>
          </div>
          {loading ? <Spinner /> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)' }}>
                    {['Ticket #', 'Service', 'Counter', 'Customer', 'Status', 'Created', 'Wait Time', 'Service Time', 'Comment'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--gray-500)', fontFamily: 'var(--font-display)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--gray-100)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tickets.length === 0 && (
                    <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>No tickets found for selected filters</td></tr>
                  )}
                  {tickets.map((t, i) => (
                    <tr key={t.id} style={{ borderTop: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--ice)'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : 'var(--gray-50)'}>
                      <td style={{ padding: '10px 14px', fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--cobalt)', whiteSpace: 'nowrap' }}>{t.ticket_number}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--navy)' }}>{t.service_name || '\u2014'}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--gray-700)' }}>{t.counter_name || '\u2014'}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--gray-700)' }}>{t.customer_name || '\u2014'}</td>
                      <td style={{ padding: '10px 14px' }}><StatusBadge status={t.status} /></td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--gray-500)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                        {formatEat(t.created_at)}
                      </td>
                      <td style={{ padding: '10px 14px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--amber)' }}>{fmtDuration(t.wait_time_secs)}</td>
                      <td style={{ padding: '10px 14px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--navy)' }}>{fmtDuration(t.duration_secs)}</td>
                      <td style={{ padding: '10px 14px', maxWidth: 200, fontSize: 12, color: 'var(--gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.agent_comment || '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
